/**
 * luxury-card.js
 * ------------------------------------------------------------------
 * Add-to-cart for the luxury product cards.
 *
 * WHAT CHANGED FROM THE OLD VERSION
 *
 * 1. The old code POSTed to /cart/add.js with NO `sections` key, so
 *    Shopify returned only the line item. Nothing on the page could
 *    re-render. It then hand-patched `.cart-count-bubble span` - a
 *    selector that does not exist in the SBL header (which uses
 *    `.sbl-cart__count`) and does not exist AT ALL when the cart
 *    started empty. Result: "Added" with a badge that never moved
 *    until the shopper refreshed.
 *
 * 2. Now it uses the Section Rendering API: the add request asks for
 *    the cart drawer, the icon bubble and the live region in one round
 *    trip, and hands the response to Dawn's own CartDrawer.renderContents(),
 *    which repaints the drawer and opens it.
 *
 * 3. It publishes Dawn's `cart-update` pub/sub event, so cart-items,
 *    cart-drawer-items and anything else listening stay in sync.
 */
(() => {
  if (window.luxuryCardActionsInitialised) return;
  window.luxuryCardActionsInitialised = true;

  const root = window.Shopify?.routes?.root || '/';
  const addToCartUrl = window.routes?.cart_add_url || `${root}cart/add.js`;

  /**
   * Ask for exactly the sections that exist on this page.
   * If Dawn's cart drawer is present we reuse its own list so the
   * ids and selectors always match what renderContents() expects.
   */
  const getSectionIds = (cartDrawer) => {
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      return cartDrawer.getSectionsToRender().map((section) => section.id);
    }

    const ids = [];
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    if (document.getElementById('cart-live-region-text')) ids.push('cart-live-region-text');
    return ids;
  };

  const getSectionInnerHTML = (html, selector = '.shopify-section') => {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return parsed.querySelector(selector)?.innerHTML ?? html;
  };

  /** Fallback repaint when there is no cart drawer on the page. */
  const renderSectionsManually = (sections) => {
    if (!sections) return;

    const bubbleHTML = sections['cart-icon-bubble'];
    const bubbleRoot = document.getElementById('shopify-section-cart-icon-bubble');

    if (bubbleHTML && bubbleRoot) {
      bubbleRoot.innerHTML = getSectionInnerHTML(bubbleHTML);
    }

    const liveHTML = sections['cart-live-region-text'];
    const liveRoot = document.getElementById('shopify-section-cart-live-region-text');

    if (liveHTML && liveRoot) {
      liveRoot.innerHTML = getSectionInnerHTML(liveHTML);
    }
  };

  const addVariant = async (variantId, sections) => {
    const response = await fetch(addToCartUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections,
        sections_url: window.location.pathname,
      }),
    });

    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(
        result.description || result.message || 'The product could not be added to the cart.'
      );
    }

    return result;
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-lux-product-action]');
    if (!button || button.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const originalText = button.textContent.trim();

    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding…';

    const cartDrawer = document.querySelector('cart-drawer');
    const sectionIds = getSectionIds(cartDrawer);

    try {
      const result = await addVariant(variantId, sectionIds);

      // 1. Repaint the drawer (Dawn's method also opens it), or fall
      //    back to patching the bubble section by hand.
      if (cartDrawer && typeof cartDrawer.renderContents === 'function' && result.sections) {
        cartDrawer.renderContents(result);
      } else {
        renderSectionsManually(result.sections);
      }

      // 2. Badge. /cart/add.js does not return the new total, so ask.
      if (typeof window.refreshCartCountUI === 'function') {
        await window.refreshCartCountUI();
      }

      // 3. Tell the rest of the theme.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'luxury-card',
          productVariantId: variantId,
          cartData: result,
        });
      }

      document.dispatchEvent(
        new CustomEvent('luxury-card:item-added', {
          bubbles: true,
          detail: { variantId: Number(variantId), result },
        })
      );

      button.textContent = 'Added ✓';
      button.classList.add('is-added');

      window.setTimeout(() => {
        button.disabled = false;
        button.classList.remove('is-loading', 'is-added');
        button.removeAttribute('aria-busy');
        button.textContent = originalText;
      }, 1600);
    } catch (error) {
      console.error('[luxury-card] Add to cart failed:', error);

      button.textContent = 'Try Again';

      window.setTimeout(() => {
        button.disabled = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        button.textContent = originalText;
      }, 1800);
    }
  });
})();
